import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckIcon,
  ChartBarIcon,
  ChartPieIcon,
  TableCellsIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
  CircleStackIcon,
  UserIcon,
  LightBulbIcon,
  Squares2X2Icon,
  GlobeAltIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

const chartTypes = [
  { id: 'bar', name: 'Bar', icon: ChartBarIcon, description: 'Compare categories' },
  { id: 'line', name: 'Line', icon: PresentationChartLineIcon, description: 'Show trends' },
  { id: 'pie', name: 'Pie', icon: ChartPieIcon, description: 'Show proportions' },
  { id: 'donut', name: 'Donut', icon: ChartPieIcon, description: 'Ring proportions' },
  { id: 'area', name: 'Area', icon: ArrowTrendingUpIcon, description: 'Volume over time' },
  { id: 'heatmap', name: 'Heatmap', icon: Squares2X2Icon, description: 'Density patterns' },
  { id: 'geo', name: 'Geo Map', icon: GlobeAltIcon, description: 'Location mapping' },
  { id: 'table', name: 'Table', icon: TableCellsIcon, description: 'Detailed data' },
  { id: 'kpi', name: 'KPI', icon: CircleStackIcon, description: 'Single metric' }
];

// Helper function to format cell values properly (handles BigQuery date objects, etc.)
function formatCellValue(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  // Handle BigQuery date/datetime objects which come as { value: '...' }
  if (typeof value === 'object') {
    // BigQuery DATE, TIMESTAMP, DATETIME often return as { value: 'YYYY-MM-DD' }
    if (value.value !== undefined) {
      return String(value.value);
    }
    // Handle Date objects
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    // For other objects, stringify
    return JSON.stringify(value);
  }
  return String(value);
}

export default function AIChartBuilder({ isOpen, onClose, dashboardId, onChartCreated }) {
  const { user, getCurrentCompanyId } = useAuthStore();
  const messagesEndRef = useRef(null);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // AI Model state
  const [provider, setProvider] = useState(user?.preferences?.defaultAIProvider || 'anthropic');
  const [model, setModel] = useState('');
  
  // Chart state
  const [chartConfig, setChartConfig] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [selectedChartType, setSelectedChartType] = useState('bar');
  const [recommendedType, setRecommendedType] = useState(null);
  const [chartName, setChartName] = useState('');
  const [previewError, setPreviewError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Data sources
  const [dataSources, setDataSources] = useState([]);
  const [wideViews, setWideViews] = useState([]);
  const [marts, setMarts] = useState([]);
  const [selectedDataSource, setSelectedDataSource] = useState('auto');
  const [selectedSourceType, setSelectedSourceType] = useState('auto'); // 'datasource', 'wideview', 'mart', or 'auto'
  const [aiSelectedSource, setAiSelectedSource] = useState(null); // Track AI's selection when auto mode
  
  // Fetch available AI models
  const { data: modelsData } = useQuery({
    queryKey: ['aiModels'],
    queryFn: () => apiHelpers.getModels(),
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });
  
  const models = modelsData?.data?.models || [];
  const providers = modelsData?.data?.providers || {};
  
  // Set default model when provider changes or models load
  useEffect(() => {
    if (modelsData?.data?.providers?.[provider]) {
      setModel(modelsData.data.providers[provider].defaultModel);
    }
  }, [provider, modelsData]);
  
  // Fetch data sources on open
  useEffect(() => {
    if (isOpen) {
      fetchChartSources();
      // Reset state
      setMessages([]);
      setChartConfig(null);
      setChartData(null);
      setChartName('');
      setPreviewError(null);
      setRecommendedType(null);
      setAiSelectedSource(null);
    }
  }, [isOpen]);
  
  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const fetchChartSources = async () => {
    try {
      const companyId = getCurrentCompanyId();
      const response = await apiHelpers.getChartSources({ companyId });
      const ds = response.data.dataSources || [];
      const wv = response.data.wideViews || [];
      const m = response.data.marts || [];
      setDataSources(ds);
      setWideViews(wv);
      setMarts(m);
      // Default to Auto-Select so AI picks the best source
      setSelectedDataSource('auto');
      setSelectedSourceType('auto');
    } catch (error) {
      console.error('Failed to fetch chart sources:', error);
    }
  };
  
  const handleSourceSelect = (sourceId) => {
    if (sourceId === 'auto') {
      setSelectedDataSource('auto');
      setSelectedSourceType('auto');
    } else {
      const isMart = sourceId.startsWith('mart_');
      const isWideView = sourceId.startsWith('wide_');
      setSelectedDataSource(sourceId);
      setSelectedSourceType(isMart ? 'mart' : (isWideView ? 'wideview' : 'datasource'));
    }
  };
  
  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    console.log('[AIChartBuilder] Sending message:', userMessage);
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setPreviewError(null);
    
    try {
      const companyId = getCurrentCompanyId();
      console.log('[AIChartBuilder] Calling API with:', { companyId, dataSourceId: selectedDataSource });
      
      // Build conversation history for context (exclude the message we just added)
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content
      }));
      
      const response = await apiHelpers.generateChartWithAI({
        message: userMessage,
        companyId,
        dataSourceId: selectedDataSource,
        currentChartType: selectedChartType,
        existingConfig: chartConfig,
        // Pass the existing query and chart name for context when modifying
        existingQuery: chartConfig?.query,
        existingChartName: chartName || chartConfig?.name,
        isEditMode: !!chartConfig, // If we have a chart config, we're editing
        conversationHistory, // Pass previous messages for context
        provider, // AI provider selection
        model // AI model selection
      });
      
      console.log('[AIChartBuilder] Raw API response:', response);
      console.log('[AIChartBuilder] Response data:', response.data);
      
      const { 
        response: aiResponse, 
        chartSuggestion, 
        recommendedChartType,
        chartData: newChartData,
        selectedSource // AI's chosen source when in auto mode
      } = response.data;
      
      console.log('[AIChartBuilder] Parsed response:', {
        hasAiResponse: !!aiResponse,
        aiResponseLength: aiResponse?.length,
        hasChartSuggestion: !!chartSuggestion,
        recommendedChartType,
        chartDataLength: newChartData?.length,
        chartDataSample: newChartData?.[0],
        selectedSource
      });
      
      // Add AI response to messages
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: aiResponse,
        chartSuggestion
      }]);
      
      // Track AI's selected source when in auto mode
      if (selectedSource) {
        console.log('[AIChartBuilder] AI selected source:', selectedSource);
        setAiSelectedSource(selectedSource);
      }
      
      // Update chart preview
      if (chartSuggestion) {
        console.log('[AIChartBuilder] Setting chartConfig:', chartSuggestion);
        setChartConfig(chartSuggestion);
        if (chartSuggestion.name) {
          setChartName(chartSuggestion.name);
        }
      }
      
      if (newChartData) {
        console.log('[AIChartBuilder] Setting chartData with', newChartData.length, 'rows');
        setChartData(newChartData);
      } else {
        console.warn('[AIChartBuilder] No chartData in response!');
      }
      
      // Always update chart type when AI recommends one
      if (recommendedChartType) {
        console.log('[AIChartBuilder] Setting chart type to:', recommendedChartType);
        setRecommendedType(recommendedChartType);
        setSelectedChartType(recommendedChartType);
      }
    } catch (error) {
      console.error('[AIChartBuilder] API error:', error);
      console.error('[AIChartBuilder] Error response:', error.response);
      console.error('[AIChartBuilder] Error message:', error.message);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error generating the chart. Please try again or rephrase your request.',
        isError: true
      }]);
      setPreviewError(error.response?.data?.error || error.message || 'Failed to generate chart');
    } finally {
      console.log('[AIChartBuilder] Request completed');
      setIsLoading(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const handleChartTypeChange = (typeId) => {
    setSelectedChartType(typeId);
    
    // If we have a config, update its type
    if (chartConfig) {
      setChartConfig(prev => ({ ...prev, type: typeId }));
      // Trigger a re-generation with the new type
      if (messages.length > 0) {
        setInput(`Change the chart to a ${typeId} chart`);
      }
    }
  };
  
  const handleSaveChart = async () => {
    if (!chartConfig || !chartName.trim()) {
      toast.error('Please provide a chart name');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const chartDataPayload = {
        name: chartName,
        type: selectedChartType,
        dashboardId,
        query: chartConfig.query,
        chartConfig: chartConfig.config,
        aiGenerated: true,
        aiPrompt: messages.find(m => m.role === 'user')?.content
      };
      
      // Determine which source to use - either user-selected or AI-selected (for auto mode)
      const effectiveSourceId = selectedDataSource === 'auto' && aiSelectedSource 
        ? aiSelectedSource.id 
        : selectedDataSource;
      const effectiveSourceType = selectedDataSource === 'auto' && aiSelectedSource
        ? aiSelectedSource.type
        : selectedSourceType;
      
      // Handle different source types
      if (effectiveSourceType === 'mart') {
        const m = marts.find(mart => mart._id === effectiveSourceId);
        chartDataPayload.martId = effectiveSourceId.replace('mart_', '');
        chartDataPayload.dbtModelName = m?.dbtModelName || aiSelectedSource?.dbtModelName;
        chartDataPayload.isMart = true;
      } else if (effectiveSourceType === 'wideview') {
        const wv = wideViews.find(w => w._id === effectiveSourceId);
        chartDataPayload.wideViewName = effectiveSourceId.replace('wide_', '');
        chartDataPayload.dbtModelName = wv?.dbtModelName || aiSelectedSource?.dbtModelName;
        chartDataPayload.isWideView = true;
      } else {
        chartDataPayload.dataSourceId = effectiveSourceId;
      }
      
      const response = await apiHelpers.createChart(chartDataPayload);
      
      toast.success('Chart created successfully!');
      onChartCreated?.(response.data.chart);
      onClose();
    } catch (error) {
      console.error('Failed to save chart:', error);
      toast.error(error.response?.data?.error || 'Failed to save chart');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Build ECharts option for preview
  const getPreviewOption = () => {
    if (!chartConfig || !chartData || chartData.length === 0) {
      return null;
    }
    
    // Table and KPI don't use ECharts - return null so they render their own preview
    if (selectedChartType === 'table' || selectedChartType === 'kpi') {
      return null;
    }
    
    const config = chartConfig.config || chartConfig;
    
    const baseOption = {
      animation: true,
      tooltip: { trigger: selectedChartType === 'pie' || selectedChartType === 'donut' ? 'item' : 'axis' },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      backgroundColor: 'transparent'
    };
    
    switch (selectedChartType) {
      case 'bar':
      case 'line':
      case 'area':
        return {
          ...baseOption,
          xAxis: {
            type: 'category',
            data: chartData.map(d => d[config.xAxis?.field || Object.keys(d)[0]]),
            axisLabel: { color: '#666' }
          },
          yAxis: {
            type: 'value',
            axisLabel: { color: '#666' }
          },
          series: [{
            name: config.series?.[0]?.name || 'Value',
            type: selectedChartType === 'area' ? 'line' : selectedChartType,
            data: chartData.map(d => d[config.series?.[0]?.field || Object.keys(d)[1]]),
            areaStyle: selectedChartType === 'area' ? { opacity: 0.3 } : undefined,
            itemStyle: { 
              color: '#6366f1',
              borderRadius: selectedChartType === 'bar' ? [4, 4, 0, 0] : undefined
            },
            smooth: selectedChartType === 'line' || selectedChartType === 'area'
          }]
        };
      
      case 'pie':
      case 'donut':
        return {
          ...baseOption,
          series: [{
            type: 'pie',
            radius: selectedChartType === 'donut' ? ['40%', '70%'] : '70%',
            data: chartData.map(d => ({
              name: d[config.xAxis?.field || Object.keys(d)[0]],
              value: d[config.series?.[0]?.field || Object.keys(d)[1]]
            })),
            label: { color: '#666' }
          }]
        };
      
      case 'heatmap': {
        const keys = Object.keys(chartData[0] || {});
        const xField = config.xAxis?.field || keys[0];
        const yField = config.yAxis?.field || keys[1];
        const valueField = config.series?.[0]?.field || keys[2] || keys[1];
        
        const xValues = [...new Set(chartData.map(d => String(d[xField])))];
        const yValues = [...new Set(chartData.map(d => String(d[yField])))];
        
        const heatmapData = chartData.map(d => {
          const xIndex = xValues.indexOf(String(d[xField]));
          const yIndex = yValues.indexOf(String(d[yField]));
          return [xIndex, yIndex, d[valueField] || 0];
        });
        
        const values = heatmapData.map(d => d[2]);
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        
        return {
          ...baseOption,
          xAxis: {
            type: 'category',
            data: xValues,
            splitArea: { show: true }
          },
          yAxis: {
            type: 'category',
            data: yValues,
            splitArea: { show: true }
          },
          visualMap: {
            min: minValue,
            max: maxValue,
            calculable: true,
            orient: 'vertical',
            right: '2%',
            top: 'center',
            inRange: {
              color: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027']
            }
          },
          series: [{
            type: 'heatmap',
            data: heatmapData,
            label: { show: heatmapData.length <= 100 }
          }]
        };
      }
      
      case 'geo': {
        // For geo, show a placeholder message
        return {
          ...baseOption,
          title: {
            text: 'Geo Map Preview',
            subtext: 'Full map will render after saving',
            left: 'center',
            top: 'center'
          }
        };
      }
      
      default:
        return null;
    }
  };
  
  // Find selected source from data sources, wide views, or marts
  const selectedDs = selectedSourceType === 'mart'
    ? marts.find(m => m._id === selectedDataSource)
    : selectedSourceType === 'wideview'
      ? wideViews.find(wv => wv._id === selectedDataSource)
      : dataSources.find(ds => ds._id === selectedDataSource);
  const previewOption = getPreviewOption();
  
  // Debug logging for preview (only when data changes)
  useEffect(() => {
    if (chartData || chartConfig) {
      console.log('[AIChartBuilder] Preview state changed:', {
        selectedChartType,
        hasChartConfig: !!chartConfig,
        hasChartData: !!chartData,
        chartDataLength: chartData?.length,
        hasPreviewOption: !!previewOption,
        previewError
      });
    }
  }, [selectedChartType, chartConfig, chartData, previewOption, previewError]);
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="flex min-h-full items-stretch justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden bg-white dark:bg-surface-900 shadow-2xl transition-all flex flex-col h-screen">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-800 bg-gradient-to-r from-primary-600 to-accent-600">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <SparklesIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-display font-semibold text-white">
                        AI Chart Builder
                      </Dialog.Title>
                      <p className="text-sm text-white/70">
                        Describe what you want to visualize
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                    <XMarkIcon className="w-6 h-6 text-white" />
                  </button>
                </div>
                
                {/* Main content - Split View */}
                <div className="flex-1 flex overflow-hidden min-h-0">
                  {/* Left side - Chart Preview */}
                  <div className="w-1/2 flex flex-col border-r border-surface-200 dark:border-surface-800 overflow-hidden">
                    {/* Chart Type Selector */}
                    <div className="p-4 border-b border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium text-surface-700 dark:text-surface-300">
                          Chart Type
                        </label>
                        {recommendedType && (
                          <div className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
                            <LightBulbIcon className="w-4 h-4" />
                            AI recommends: <span className="font-medium capitalize">{recommendedType}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {chartTypes.map(type => {
                          const Icon = type.icon;
                          const isRecommended = type.id === recommendedType;
                          const isSelected = type.id === selectedChartType;
                          
                          return (
                            <button
                              key={type.id}
                              onClick={() => handleChartTypeChange(type.id)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                  : isRecommended
                                    ? 'border-primary-300 bg-primary-25 dark:bg-primary-900/10 text-surface-700 dark:text-surface-300'
                                    : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:border-surface-400'
                              }`}
                              title={type.description}
                            >
                              <Icon className="w-4 h-4" />
                              {type.name}
                              {isRecommended && !isSelected && (
                                <span className="w-2 h-2 rounded-full bg-primary-500" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Data Source Selector */}
                    <div className="p-4 border-b border-surface-200 dark:border-surface-800">
                      <label className="text-sm font-medium text-surface-700 dark:text-surface-300 block mb-2">
                        Data Source
                      </label>
                      <select
                        value={selectedDataSource}
                        onChange={(e) => handleSourceSelect(e.target.value)}
                        className="input w-full"
                      >
                        <option value="auto">✨ Auto-Select (AI chooses best source)</option>
                        {marts.length > 0 && (
                          <optgroup label="⭐ Marts (Analytics-Ready)">
                            {marts.map(m => (
                              <option key={m._id} value={m._id}>{m.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {dataSources.length > 0 && (
                          <optgroup label="📊 Data Sources">
                            {dataSources.map(ds => (
                              <option key={ds._id} value={ds._id}>{ds.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {wideViews.length > 0 && (
                          <optgroup label="🔗 Wide Views (Joined Tables)">
                            {wideViews.map(wv => (
                              <option key={wv._id} value={wv._id}>{wv.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                      {selectedDataSource === 'auto' ? (
                        aiSelectedSource ? (
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                            <CheckIcon className="w-3 h-3" />
                            AI selected: <span className="font-medium">{aiSelectedSource.name}</span>
                          </p>
                        ) : (
                          <p className="text-xs text-primary-600 dark:text-primary-400 mt-1 flex items-center gap-1">
                            <SparklesIcon className="w-3 h-3" />
                            AI will analyze your request and pick the best data source
                          </p>
                        )
                      ) : selectedDs && (
                        <p className="text-xs text-surface-500 mt-1">
                          {selectedSourceType === 'mart' ? (
                            <>⭐ Pre-aggregated mart with {selectedDs.dimensions?.length || 0} dimensions and {selectedDs.measures?.length || 0} measures</>
                          ) : selectedSourceType === 'wideview' ? (
                            <>✨ Wide view with {selectedDs.schemaInfo?.tables?.[0]?.columns?.length || 0} columns</>
                          ) : (
                            <>{selectedDs.schemaInfo?.tables?.[0]?.columns?.length || 0} columns available</>
                          )}
                        </p>
                      )}
                    </div>
                    
                    {/* Chart Preview */}
                    <div className="flex-1 p-6 flex flex-col min-h-0 overflow-hidden">
                      <div className="flex items-center justify-between mb-4 flex-shrink-0">
                        <h3 className="font-medium text-surface-900 dark:text-white">
                          Preview
                        </h3>
                        {chartConfig && (
                          <button
                            onClick={() => setInput('Refresh the chart with latest data')}
                            className="btn-ghost btn-sm"
                          >
                            <ArrowPathIcon className="w-4 h-4 mr-1" />
                            Refresh
                          </button>
                        )}
                      </div>
                      
                      <div className="flex-1 min-h-0 rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 overflow-hidden">
                        {!chartConfig && !chartData ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-4">
                              <ChartBarIcon className="w-8 h-8 text-surface-400" />
                            </div>
                            <h4 className="font-medium text-surface-900 dark:text-white mb-2">
                              No chart yet
                            </h4>
                            <p className="text-sm text-surface-500 max-w-xs">
                              Describe what you want to see in the chat, and I'll create a chart for you.
                            </p>
                          </div>
                        ) : previewError ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                              <XMarkIcon className="w-8 h-8 text-red-500" />
                            </div>
                            <h4 className="font-medium text-red-600 dark:text-red-400 mb-2">
                              Preview Error
                            </h4>
                            <p className="text-sm text-surface-500 max-w-xs">
                              {previewError}
                            </p>
                          </div>
                        ) : selectedChartType === 'table' && chartData && chartData.length > 0 ? (
                          <div className="h-full overflow-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-surface-100 dark:bg-surface-700 sticky top-0">
                                <tr>
                                  {chartData[0] && Object.keys(chartData[0]).map(key => (
                                    <th key={key} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400">
                                      {key}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {chartData.slice(0, 50).map((row, i) => (
                                  <tr key={i} className="border-b border-surface-100 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-700/50">
                                    {Object.values(row).map((value, j) => (
                                      <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300">
                                        {formatCellValue(value)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {chartData.length > 50 && (
                              <div className="px-4 py-2 text-center text-xs text-surface-500 bg-surface-50 dark:bg-surface-800 border-t border-surface-200 dark:border-surface-700">
                                Showing 50 of {chartData.length} rows
                              </div>
                            )}
                          </div>
                        ) : selectedChartType === 'kpi' && chartData && chartData.length > 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="text-5xl font-display font-bold text-surface-900 dark:text-white mb-2">
                              {(() => {
                                const config = chartConfig?.config || chartConfig;
                                const valueField = config?.kpiConfig?.valueField || config?.series?.[0]?.field || Object.keys(chartData[0])[0];
                                const value = chartData[0]?.[valueField];
                                const prefix = config?.kpiConfig?.prefix || '';
                                const suffix = config?.kpiConfig?.suffix || '';
                                const formattedValue = typeof value === 'number' 
                                  ? new Intl.NumberFormat().format(value)
                                  : value;
                                return `${prefix}${formattedValue}${suffix}`;
                              })()}
                            </div>
                            <p className="text-surface-500">
                              {(() => {
                                const config = chartConfig?.config || chartConfig;
                                return config?.kpiConfig?.label || config?.name || Object.keys(chartData[0])[0];
                              })()}
                            </p>
                          </div>
                        ) : previewOption ? (
                          <div className="h-full p-4">
                            <ReactECharts
                              option={previewOption}
                              style={{ height: '100%', width: '100%' }}
                              opts={{ renderer: 'svg' }}
                            />
                          </div>
                        ) : chartConfig && chartData ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-3">
                              <TableCellsIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                            </div>
                            <p className="text-sm text-surface-500">
                              Preview not available for this chart type
                            </p>
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Save Section */}
                    {chartConfig && chartData && (
                      <div className="p-4 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={chartName}
                            onChange={(e) => setChartName(e.target.value)}
                            placeholder="Enter chart name..."
                            className="input flex-1"
                          />
                          <button
                            onClick={handleSaveChart}
                            disabled={isSaving || !chartName.trim()}
                            className="btn-primary"
                          >
                            {isSaving ? (
                              <>
                                <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <CheckIcon className="w-5 h-5 mr-2" />
                                Save Chart
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Right side - AI Chat */}
                  <div className="w-1/2 flex flex-col overflow-hidden">
                    {/* AI Model Selector */}
                    <div className="p-3 border-b border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                      <div className="flex items-center gap-2">
                        <CpuChipIcon className="w-4 h-4 text-surface-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-surface-600 dark:text-surface-400">Model:</span>
                        <select
                          value={provider}
                          onChange={(e) => setProvider(e.target.value)}
                          className="input py-1.5 text-sm w-28"
                        >
                          {Object.entries(providers).map(([key, value]) => (
                            <option key={key} value={key}>{value.name}</option>
                          ))}
                        </select>
                        <select
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          className="input py-1.5 text-sm flex-1"
                        >
                          {models
                            .filter(m => m.provider === provider)
                            .map(m => (
                              <option key={m.id} value={m.id}>
                                {m.name} {m.description ? `- ${m.description}` : ''}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Chat Messages */}
                    <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                      {messages.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-4">
                            <SparklesIcon className="w-8 h-8 text-white" />
                          </div>
                          <h3 className="text-lg font-display font-bold text-surface-900 dark:text-white mb-2">
                            What would you like to visualize?
                          </h3>
                          <p className="text-surface-500 max-w-sm mb-6">
                            Tell me what data you want to see, and I'll create the perfect chart for you.
                          </p>
                          
                          {/* Suggestion chips */}
                          <div className="flex flex-wrap gap-2 justify-center max-w-md">
                            {[
                              'Show sales by month',
                              'Top 10 products by revenue',
                              'Customer growth over time',
                              'Distribution of order values'
                            ].map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => setInput(suggestion)}
                                className="px-3 py-2 rounded-full bg-surface-100 dark:bg-surface-800 text-sm text-surface-600 dark:text-surface-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 transition-colors"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <>
                          {messages.map((msg, i) => (
                            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                msg.role === 'assistant' 
                                  ? 'bg-gradient-to-br from-primary-500 to-accent-500' 
                                  : 'bg-surface-200 dark:bg-surface-700'
                              }`}>
                                {msg.role === 'assistant' 
                                  ? <SparklesIcon className="w-4 h-4 text-white" />
                                  : <UserIcon className="w-4 h-4 text-surface-600 dark:text-surface-400" />
                                }
                              </div>
                              
                              <div className={`max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                                <div className={`inline-block px-4 py-3 rounded-2xl ${
                                  msg.role === 'assistant'
                                    ? msg.isError 
                                      ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                      : 'bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white'
                                    : 'bg-primary-600 text-white'
                                }`}>
                                  {msg.role === 'assistant' ? (
                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {msg.content}
                                      </ReactMarkdown>
                                    </div>
                                  ) : (
                                    <p className="whitespace-pre-wrap">{msg.content}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {isLoading && (
                            <div className="flex gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                                <SparklesIcon className="w-4 h-4 text-white" />
                              </div>
                              <div className="bg-surface-100 dark:bg-surface-800 rounded-2xl px-4 py-3">
                                <div className="flex gap-1">
                                  <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                  <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                  <div className="w-2 h-2 bg-surface-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    
                    {/* Chat Input */}
                    <div className="p-4 border-t border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50">
                      <div className="flex gap-3">
                        <textarea
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={handleKeyDown}
                          placeholder="Describe what you want to visualize..."
                          rows={2}
                          className="flex-1 input resize-none"
                        />
                        <button
                          onClick={handleSend}
                          disabled={!input.trim() || isLoading}
                          className="btn-primary self-end px-4"
                        >
                          <PaperAirplaneIcon className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="text-xs text-surface-500 mt-2">
                        Press Enter to send, Shift+Enter for new line
                      </p>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

